import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { generateOrderPdfBlob } from '../lib/orderPdf'
import type { Company, OrderStatus, PurchaseOrderWithItems, Supplier } from '../types'

interface ItemInput {
  product_id: string
  sku: string
  product_name: string
  quantity: number
  unit_cost: number
}

export function usePurchaseOrders() {
  const [orders, setOrders] = useState<PurchaseOrderWithItems[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  async function fetchData() {
    const { data, error } = await supabase
      .from('purchase_orders')
      .select('*, suppliers(*), purchase_order_items(*)')
      .order('created_at', { ascending: false })

    if (error) {
      setError(error.message)
      setLoading(false)
      return
    }

    setOrders((data ?? []) as PurchaseOrderWithItems[])
    setLoading(false)
  }

  // Verifica recebimento ao vivo: puxa o saldo atual do Tiny só para os
  // itens do pedido e reconcilia na hora. Garantia 100% sob demanda.
  async function checkReceipt(orderId: string): Promise<{ received: boolean } | { error: string }> {
    const { data, error } = await supabase.functions.invoke('tiny-receive-check', {
      body: { orderId },
    })
    if (error) return { error: error.message }
    await fetchData()
    return data as { received: boolean }
  }

  useEffect(() => {
    // Reconcilia no mount (rede de segurança extra ao webhook/cron) antes de listar.
    supabase.rpc('fn_auto_receive_orders').then(fetchData, fetchData)

    // Debounce: agrupa rajadas de eventos (ex.: insert de vários itens) num único refetch (egress).
    // 5s para absorver bem a rajada de inserção de itens ao emitir um pedido.
    let timer: ReturnType<typeof setTimeout> | null = null
    const scheduleRefetch = () => {
      if (timer) clearTimeout(timer)
      timer = setTimeout(fetchData, 5000)
    }
    const channel = supabase
      .channel('orders_realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'purchase_orders' }, scheduleRefetch)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'purchase_order_items' }, scheduleRefetch)
      .subscribe()

    return () => { if (timer) clearTimeout(timer); supabase.removeChannel(channel) }
  }, [])

  return { orders, loading, error, refresh: fetchData, checkReceipt }
}

export async function emitPurchaseOrder(opts: {
  companyId: string
  company: Company
  supplier: Supplier
  items: ItemInput[]
  notes?: string
  order_type?: 'compra' | 'consignacao'
}): Promise<{ order: PurchaseOrderWithItems; pdfPath: string } | { error: string }> {
  const { companyId, company, supplier, items, notes, order_type = 'compra' } = opts

  if (items.length === 0) return { error: 'Nenhum item selecionado.' }

  const total = items.reduce((sum, i) => sum + i.quantity * i.unit_cost, 0)

  const { data: numberData, error: numberErr } = await supabase
    .rpc('fn_next_order_number', { p_company_id: companyId })
  if (numberErr) return { error: numberErr.message }

  const orderNumber = numberData as string

  // Baseline de estoque: saldo atual de cada produto no momento da emissão.
  // Serve de referência para o auto-recebimento (current_stock > stock_at_emission).
  const productIds = items.map(i => i.product_id).filter(Boolean)
  const { data: stockRows } = await supabase
    .from('products').select('id, current_stock').in('id', productIds)
  const stockMap = new Map((stockRows ?? []).map(r => [r.id as string, r.current_stock as number]))

  const { data: orderData, error: orderErr } = await supabase
    .from('purchase_orders')
    .insert({
      company_id: companyId,
      supplier_id: supplier.id,
      order_number: orderNumber,
      status: 'Pendente',
      total_value: total,
      notes: notes ?? null,
      order_type,
    })
    .select()
    .single()
  if (orderErr || !orderData) return { error: orderErr?.message ?? 'Erro ao criar pedido' }

  const itemsInsert = items.map(i => ({
    company_id: companyId,
    purchase_order_id: orderData.id,
    product_id: i.product_id,
    sku: i.sku,
    product_name: i.product_name,
    quantity: i.quantity,
    unit_cost: i.unit_cost,
    stock_at_emission: stockMap.get(i.product_id) ?? null,
  }))

  const { error: itemsErr } = await supabase.from('purchase_order_items').insert(itemsInsert)
  if (itemsErr) return { error: itemsErr.message }

  await Promise.all(items.map(i =>
    supabase.from('products').update({ approved_qty: i.quantity }).eq('id', i.product_id)
  ))

  const { data: fullOrder, error: fetchErr } = await supabase
    .from('purchase_orders')
    .select('*, suppliers(*), purchase_order_items(*)')
    .eq('id', orderData.id)
    .single()
  if (fetchErr || !fullOrder) return { error: fetchErr?.message ?? 'Erro ao recarregar pedido' }

  const order = fullOrder as PurchaseOrderWithItems

  const pdfBlob = await generateOrderPdfBlob({ order, supplier, company })
  const pdfPath = `${companyId}/${orderNumber}.pdf`

  const { error: uploadErr } = await supabase.storage
    .from('purchase-orders')
    .upload(pdfPath, pdfBlob, { contentType: 'application/pdf', upsert: true })
  if (uploadErr) return { error: `Pedido criado, mas falha ao salvar PDF: ${uploadErr.message}` }

  await supabase.from('purchase_orders').update({ pdf_path: pdfPath }).eq('id', order.id)

  return { order: { ...order, pdf_path: pdfPath }, pdfPath }
}

export async function getOrderPdfUrl(pdfPath: string, expiresIn = 60 * 60 * 24): Promise<string | null> {
  const { data, error } = await supabase.storage
    .from('purchase-orders')
    .createSignedUrl(pdfPath, expiresIn)
  if (error || !data) return null
  return data.signedUrl
}

export async function deleteOrder(orderId: string, pdfPath: string | null): Promise<string | null> {
  if (pdfPath) {
    await supabase.storage.from('purchase-orders').remove([pdfPath])
  }
  const { error: itemsErr } = await supabase
    .from('purchase_order_items').delete().eq('purchase_order_id', orderId)
  if (itemsErr) return itemsErr.message

  const { error } = await supabase
    .from('purchase_orders').delete().eq('id', orderId)
  return error?.message ?? null
}

export async function updateOrderStatus(orderId: string, status: OrderStatus): Promise<string | null> {
  const extra: Record<string, string> = {}
  if (status === 'Enviado')  extra.sent_at     = new Date().toISOString()
  if (status === 'Recebido') extra.received_at = new Date().toISOString()

  const { error } = await supabase
    .from('purchase_orders')
    .update({ status, ...extra })
    .eq('id', orderId)
  return error?.message ?? null
}
